import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubSettingsFooter, HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { HubUserSearchableMultiSelect } from "@/components/settings/HubUserSearchableMultiSelect";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Card, CardBody } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { formatTimestamp } from "@/lib/utils";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";

import { SalesOrderFormSettingsSections } from "../sales-orders/SalesOrderFormSettingsSections";
import {
  fetchInsideSalesApprovalSettings,
  INSIDE_SALES_APPROVAL_SETTINGS_QUERY_KEY,
  patchInsideSalesApprovalSettings,
  type ApprovalSettings,
} from "./insideSalesApprovalsApi";
import { QuoteSettingsSection } from "./QuoteSettingsSection";

type SettingsDraft = Pick<
  ApprovalSettings,
  | "processingUserIds"
  | "preapprovalUserIds"
  | "orderApprovalUserIds"
  | "quoteApprovalUserIds"
  | "wirelessPortalAccountUserIds"
>;

const EMPTY_DRAFT: SettingsDraft = {
  processingUserIds: [],
  preapprovalUserIds: [],
  orderApprovalUserIds: [],
  quoteApprovalUserIds: [],
  wirelessPortalAccountUserIds: [],
};

export function InsideSalesSettingsPage() {
  const queryClient = useQueryClient();
  const canManage = useHubModuleAdmin("inside_sales");
  const [draft, setDraft] = useState<SettingsDraft>(EMPTY_DRAFT);
  const settingsQuery = useQuery({
    queryKey: INSIDE_SALES_APPROVAL_SETTINGS_QUERY_KEY,
    queryFn: fetchInsideSalesApprovalSettings,
  });

  useEffect(() => {
    if (!settingsQuery.data) return;
    setDraft({
      processingUserIds: settingsQuery.data.processingUserIds,
      preapprovalUserIds: settingsQuery.data.preapprovalUserIds,
      orderApprovalUserIds: settingsQuery.data.orderApprovalUserIds,
      quoteApprovalUserIds: settingsQuery.data.quoteApprovalUserIds,
      wirelessPortalAccountUserIds: settingsQuery.data.wirelessPortalAccountUserIds,
    });
  }, [settingsQuery.data]);

  const users = settingsQuery.data?.userOptions ?? [];
  const dirty = useMemo(() => {
    if (!settingsQuery.data) return false;
    return JSON.stringify(draft) !== JSON.stringify({
      processingUserIds: settingsQuery.data.processingUserIds,
      preapprovalUserIds: settingsQuery.data.preapprovalUserIds,
      orderApprovalUserIds: settingsQuery.data.orderApprovalUserIds,
      quoteApprovalUserIds: settingsQuery.data.quoteApprovalUserIds,
      wirelessPortalAccountUserIds: settingsQuery.data.wirelessPortalAccountUserIds,
    });
  }, [draft, settingsQuery.data]);
  const saveMutation = useMutation({
    mutationFn: () => patchInsideSalesApprovalSettings(draft),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: INSIDE_SALES_APPROVAL_SETTINGS_QUERY_KEY }),
  });

  return (
    <div className="mx-auto min-h-0 w-full max-w-3xl flex-1 space-y-3 overflow-y-auto px-1 pb-6">
      <PageTitleWithHelp hoverHint="Configure approval distribution pools and sales-order defaults. Inside Sales module administrator access is required to save approval lists. Payment terms and shipping defaults require integrator admin access.">
        Settings
      </PageTitleWithHelp>
      {settingsQuery.isLoading ? <Card><CardBody className="py-6 text-sm text-slate-600">Loading settings...</CardBody></Card> : null}
      {settingsQuery.isError ? <Card><CardBody className="py-6 text-sm text-rose-700">{settingsQuery.error.message}</CardBody></Card> : null}
      {!canManage ? <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">View only. Inside Sales module administrator access is required to edit these lists.</div> : null}

      <HubSettingsSection title="Processing and doer pool" hoverHint="Users eligible to receive new requests and prepare the initial order or quote draft.">
        <HubUserSearchableMultiSelect
          id="inside-sales-processing-users"
          users={users}
          selectedUserIds={draft.processingUserIds}
          onSelectedUserIdsChange={(processingUserIds) => setDraft((current) => ({ ...current, processingUserIds }))}
          disabled={!canManage}
          noneSelectedLabel="No processing users"
        />
      </HubSettingsSection>
      <HubSettingsSection title="Pre-approvers" hoverHint="Users eligible for the pre-approval review step for orders and quotes.">
        <HubUserSearchableMultiSelect
          id="inside-sales-preapproval-users"
          users={users}
          selectedUserIds={draft.preapprovalUserIds}
          onSelectedUserIdsChange={(preapprovalUserIds) => setDraft((current) => ({ ...current, preapprovalUserIds }))}
          disabled={!canManage}
          noneSelectedLabel="No pre-approvers"
        />
      </HubSettingsSection>
      <HubSettingsSection title="Order final approvers" hoverHint="Users eligible to provide final approval for sales orders.">
        <HubUserSearchableMultiSelect
          id="inside-sales-order-approval-users"
          users={users}
          selectedUserIds={draft.orderApprovalUserIds}
          onSelectedUserIdsChange={(orderApprovalUserIds) => setDraft((current) => ({ ...current, orderApprovalUserIds }))}
          disabled={!canManage}
          noneSelectedLabel="No order approvers"
        />
      </HubSettingsSection>
      <HubSettingsSection title="Quote final approvers" hoverHint="Users eligible to provide final approval for quotes.">
        <HubUserSearchableMultiSelect
          id="inside-sales-quote-approval-users"
          users={users}
          selectedUserIds={draft.quoteApprovalUserIds}
          onSelectedUserIdsChange={(quoteApprovalUserIds) => setDraft((current) => ({ ...current, quoteApprovalUserIds }))}
          disabled={!canManage}
          noneSelectedLabel="No quote approvers"
        />
      </HubSettingsSection>
      <HubSettingsSection
        title="New wireless portal orgs distribution list"
        hoverHint="Users emailed when a new wireless portal organization is created on sales order approval."
      >
        <HubUserSearchableMultiSelect
          id="inside-sales-wireless-portal-account-users"
          users={users}
          selectedUserIds={draft.wirelessPortalAccountUserIds}
          onSelectedUserIdsChange={(wirelessPortalAccountUserIds) =>
            setDraft((current) => ({ ...current, wirelessPortalAccountUserIds }))
          }
          disabled={!canManage}
          noneSelectedLabel="No new wireless portal org recipients"
        />
      </HubSettingsSection>

      <HubSettingsFooter>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-slate-600">
            {saveMutation.isError
              ? saveMutation.error instanceof Error ? saveMutation.error.message : "Could not save settings."
              : saveMutation.isSuccess
                ? "Settings saved."
                : settingsQuery.data?.updatedAt
                  ? `Last saved ${formatTimestamp(settingsQuery.data.updatedAt)}`
                  : "Not saved yet"}
          </div>
          <HubSaveButton
            type="button"
            dirty={dirty}
            disabled={!canManage}
            blockedHint="Inside Sales module administrator access is required to edit these lists."
            isLoading={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            Save approval settings
          </HubSaveButton>
        </div>
      </HubSettingsFooter>

      <QuoteSettingsSection />
      <SalesOrderFormSettingsSections />
    </div>
  );
}
